export function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function normalizeNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

export function formatMoney(value) {
  return `${normalizeNumber(value).toFixed(2)} €`;
}

// IVA junto a «Impuestos» en los tickets (como api-rest account_print_payloads.tax_rate_label):
// el tipo si todos los productos llevan el mismo; si no, el medio (cuota / base). «10%», «13,25%».
export function formatTaxRateLabel(rates = [], baseAmount = 0, taxAmount = 0) {
  const format = (rate) => `${Number(rate.toFixed(2)).toString().replace('.', ',')}%`;
  const distinct = [...new Set(rates
    .filter((rate) => rate !== null && rate !== undefined && rate !== '')
    .map((rate) => Math.round(normalizeNumber(rate) * 100) / 100))];
  if (distinct.length === 1) return format(distinct[0]);
  const base = normalizeNumber(baseAmount);
  if (base <= 0) return '';
  return format(Math.round((normalizeNumber(taxAmount) / base) * 10000) / 100);
}

export function formatDateTime(value) {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('es-ES', {
    timeZone: 'Europe/Madrid',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function escapeText(value) {
  return String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
}

export function htmlToPrintableText(html) {
  const markup = String(html ?? '').trim();
  if (!markup) {
    return '';
  }

  const normalizePrintableText = (value) => String(value || '')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    const parser = new window.DOMParser();
    const documentNode = parser.parseFromString(markup, 'text/html');

    documentNode
      .querySelectorAll('br, hr')
      .forEach((element) => element.replaceWith('\n'));

    documentNode
      .querySelectorAll('address, article, aside, blockquote, div, dl, fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, header, li, main, nav, ol, p, pre, section, table, tr, ul')
      .forEach((element) => element.append('\n'));

    return normalizePrintableText(documentNode.body?.textContent || '');
  }

  return normalizePrintableText(
    markup
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/\s*(p|div|section|article|header|footer|li|tr|h[1-6])\s*>/gi, '\n')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, ' ')
  );
}

function renderMetaRows(metaRows = []) {
  return metaRows
    .filter((row) => row && (row.label || row.value))
    .map((row) => `
      <div class="meta-row">
        <span class="label">${escapeHtml(row.label || '')}:</span>
        <span class="value">${escapeHtml(row.value || '')}</span>
      </div>
    `)
    .join('');
}

function renderDetailLines(details = []) {
  return details
    .filter(Boolean)
    .map((line, index) => `<div class="item-steps${index === 0 ? ' item-steps--first' : ''}">${escapeHtml(line)}</div>`)
    .join('');
}

function renderItems(items = [], showPrices = true) {
  return items
    .map((item) => {
      const quantity = normalizeNumber(item.quantity ?? item.amount ?? item.count ?? 0);
      const name = item.name || item.plate_name || item.product_name_snapshot || 'Producto';
      const unitPrice = item.unitPrice ?? item.unit_price ?? item.price ?? null;
      const total = item.total ?? item.total_amount ?? null;
      const details = Array.isArray(item.details) ? item.details : [];
      const note = item.note || item.comment || '';

      return `
        <div class="item-row${showPrices ? '' : ' item-row--compact'}">
          <div class="item-qty">${quantity > 0 ? `${quantity}x` : ''}</div>
          <div class="item-name">
            <div class="bold">${escapeHtml(name)}</div>
            ${renderDetailLines(details)}
            ${note ? `<div class="item-steps">${escapeHtml(note)}</div>` : ''}
          </div>
          ${showPrices ? `<div class="item-price">${unitPrice == null ? '' : escapeHtml(formatMoney(unitPrice))}</div>` : ''}
          ${showPrices ? `<div class="item-total">${total == null ? '' : escapeHtml(formatMoney(total))}</div>` : ''}
        </div>
      `;
    })
    .join('');
}

function renderSummaryRows(summaryRows = []) {
  return summaryRows
    .filter((row) => row && (row.label || row.value))
    .map((row) => `
      <div class="summary-row${row.emphasis ? ' bold' : ''}">
        <span>${escapeHtml(row.label || '')}</span>
        <span>${escapeHtml(row.value || '')}</span>
      </div>
    `)
    .join('');
}

export function buildTicketReceiptHtml({
  restaurantName = 'Eat with Us TPV',
  restaurantAddress = '',
  restaurantDetails = '',
  title = 'TICKET',
  metaRows = [],
  items = [],
  itemsHeader = ['Producto', 'PVP', 'Total'],
  showPrices = true,
  summaryRows = [],
  totalLabel = 'TOTAL',
  totalValue = null,
  footerTitle = '¡Gracias por su visita!',
  footerSubtitle = '',
  widthMm = 72,
} = {}) {
  const itemsHtml = renderItems(items, showPrices);
  const summaryHtml = renderSummaryRows(summaryRows);
  const grandTotal = totalValue == null ? '' : formatMoney(totalValue);
  const hasTotalsSection = Boolean(summaryHtml) || totalValue != null;

  return `
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${escapeHtml(title)}</title>
        <style>
          @page { margin: 0; size: 80mm auto; }
          html, body { margin: 0; padding: 0; height: auto; background: #fff; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            width: ${widthMm}mm;
            margin: 0 auto;
            padding: 6mm 2mm 5mm;
            font-size: 11px;
            color: #000;
            line-height: 1.2;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .ticket {
            display: flex;
            flex-direction: column;
            gap: 0;
          }
          .center { text-align: center; }
          .restaurant-name {
            font-size: 20px;
            font-weight: 900;
            line-height: 1.0;
            letter-spacing: 0.2px;
            margin-bottom: 1mm;
          }
          .restaurant-info {
            font-size: 12px;
            font-weight: 900;
            text-transform: uppercase;
            margin-bottom: 1mm;
          }
          .ticket-title {
            font-size: 13px;
            font-weight: 900;
            text-transform: uppercase;
            margin-bottom: 2mm;
          }
          .section-divider {
            border-top: 3px dotted #000;
            margin: 3mm 0;
          }
          .ticket-meta {
            margin-bottom: 1mm;
          }
          .meta-row {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 1mm;
            font-size: 11px;
            font-weight: 900;
          }
          .label {
            flex: 0 0 auto;
            font-weight: 900;
            text-transform: uppercase;
          }
          .value {
            flex: 1 1 auto;
            text-align: right;
            font-weight: 900;
          }
          .items-header {
            display: grid;
            grid-template-columns: 11% 52% 18% 19%;
            align-items: center;
            padding: 1mm 0 1.2mm;
            margin-top: 2mm;
            margin-bottom: 1.2mm;
            border-bottom: 3px solid #000;
            font-size: 10px;
            font-weight: 900;
            text-transform: uppercase;
          }
          .items-header--compact {
            grid-template-columns: 12% 88%;
          }
          .item-row {
            display: grid;
            grid-template-columns: 11% 52% 18% 19%;
            align-items: start;
            gap: 0;
            margin-bottom: 1.4mm;
            font-size: 11px;
          }
          .item-row--compact {
            grid-template-columns: 12% 88%;
          }
          .item-qty {
            font-weight: 900;
            line-height: 1.1;
            padding-top: 0.3mm;
          }
          .item-name {
            font-weight: 900;
            padding-right: 1mm;
          }
          .item-name .bold {
            font-size: 11px;
            line-height: 1.08;
            text-transform: uppercase;
          }
          .item-steps {
            font-size: 8.5px;
            line-height: 1.05;
            margin-top: 0.6mm;
            padding-left: 2mm;
            border-left: 2px solid #000;
            white-space: pre-wrap;
            font-weight: 700;
          }
          .item-steps--first {
            margin-top: 1mm;
          }
          .item-price,
          .item-total {
            text-align: right;
            font-weight: 900;
            white-space: nowrap;
          }
          .item-price {
            padding-right: 1mm;
          }
          .divider {
            border-top: 3px dotted #000;
            margin: 3mm 0;
          }
          .totals-container {
            margin-top: 1mm;
            font-weight: 900;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 1mm;
            font-size: 10px;
            font-weight: 900;
          }
          .summary-row.bold { font-weight: 900; }
          .grand-total {
            display: flex;
            justify-content: space-between;
            margin-top: 2mm;
            padding-top: 2mm;
            font-size: 17px;
            font-weight: 900;
            letter-spacing: -1px;
          }
          .footer-section { margin-top: 7mm; text-align: center; padding-top: 3mm; }
          .thanks-msg { font-weight: 900; font-size: 12px; text-transform: uppercase; }
          .web-link { font-size: 9px; font-weight: bold; margin-top: 1mm; }
          .bold { font-weight: 900; }
        </style>
      </head>
      <body>
        <div class="ticket">
          <div class="center">
            <div class="restaurant-name">${escapeHtml(restaurantName)}</div>
            ${restaurantAddress ? `<div class="restaurant-info">${escapeHtml(restaurantAddress)}</div>` : ''}
            ${restaurantDetails ? `<div class="restaurant-info">${escapeHtml(restaurantDetails)}</div>` : ''}
            <div class="ticket-title">${escapeHtml(title)}</div>
          </div>

          <div class="section-divider"></div>

          <div class="ticket-meta">
            ${renderMetaRows(metaRows)}
          </div>

          <div class="items-header${showPrices ? '' : ' items-header--compact'}">
            <div></div>
            <div>${escapeHtml(itemsHeader[0] || 'Producto')}</div>
            ${showPrices ? `<div class="center">${escapeHtml(itemsHeader[1] || 'PVP')}</div>` : ''}
            ${showPrices ? `<div class="center">${escapeHtml(itemsHeader[2] || 'TOTAL')}</div>` : ''}
          </div>

          <div class="items-list">
            ${itemsHtml}
          </div>

          ${hasTotalsSection ? '<div class="divider"></div>' : ''}

          ${hasTotalsSection ? `
            <div class="totals-container">
              ${summaryHtml}
              ${totalValue != null ? `
                <div class="grand-total">
                  <span>${escapeHtml(totalLabel)}</span>
                  <span>${escapeHtml(grandTotal)}</span>
                </div>
              ` : ''}
            </div>
          ` : ''}

          <div class="footer-section">
            <div class="thanks-msg">${escapeHtml(footerTitle)}</div>
            ${footerSubtitle ? `<div class="web-link">${escapeHtml(footerSubtitle)}</div>` : ''}
          </div>
        </div>
      </body>
    </html>
  `;
}

export async function printTicketHtml(html) {
  if (typeof window === 'undefined') return false;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.opacity = '0';
  frame.style.pointerEvents = 'none';
  frame.style.zIndex = '-1';

  const cleanup = () => {
    window.setTimeout(() => {
      frame.remove();
    }, 500);
  };

  return new Promise((resolve) => {
    frame.onload = () => {
      try {
        const frameWindow = frame.contentWindow;
        if (!frameWindow) {
          cleanup();
          resolve(false);
          return;
        }
        frameWindow.focus();
        frameWindow.print();
        cleanup();
        resolve(true);
      } catch (error) {
        console.warn('No se pudo imprimir el ticket:', error);
        cleanup();
        resolve(false);
      }
    };

    frame.srcdoc = html;
    document.body.appendChild(frame);
  });
}
